import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { useMfaStore, MFA_REDIRECT_PATH } from '@/stores/mfaStore';
import { Card } from '@/components/ui';
import { useTheme } from '@/hooks/useTheme';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  requireAuth?: boolean;
  fallback?: React.ReactNode;
}

export function ProtectedRoute({
  children,
  redirectTo = '/login',
  requireAuth = true,
  fallback
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();

  // Check MFA state from Zustand store
  const mfaRequired = useMfaStore((state) => state.mfaRequired);

  // Show loading state while checking authentication
  if (isLoading) {
    return fallback || (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-secondary-900' : 'bg-white'}`}>
        <Card className="p-8 text-center max-w-md w-full">
          <div className={`animate-spin rounded-full h-8 w-8 border-2 border-transparent border-t-current mx-auto mb-4 ${
            isDark ? 'text-primary-400' : 'text-primary-600'
          }`}></div>
          <h3 className={`text-lg font-semibold mb-2 ${
            isDark ? 'text-secondary-100' : 'text-secondary-900'
          }`}>
            Authenticating
          </h3>
          <p className={isDark ? 'text-secondary-400' : 'text-secondary-600'}>
            Please wait while we verify your session...
          </p>
        </Card>
      </div>
    );
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // Save the attempted location for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If MFA is required and we're not already on the MFA page, redirect to MFA
  if (requireAuth && mfaRequired && location.pathname !== MFA_REDIRECT_PATH) {
    return <Navigate to={MFA_REDIRECT_PATH} replace />;
  }

  // If authentication is not required but user is authenticated (e.g., login page)
  if (!requireAuth && isAuthenticated) {
    // Redirect authenticated users away from login/signup pages
    const from = location.state?.from?.pathname || '/dashboard';
    return <Navigate to={from} replace />;
  }

  // Render children if all conditions are met
  return <>{children}</>;
}

// Convenience component for routes that require authentication
export function AuthenticatedRoute({ children, ...props }: Omit<ProtectedRouteProps, 'requireAuth'>) {
  return (
    <ProtectedRoute requireAuth={true} {...props}>
      {children}
    </ProtectedRoute>
  );
}

// Convenience component for routes that should only be accessible to unauthenticated users
export function UnauthenticatedRoute({ children, ...props }: Omit<ProtectedRouteProps, 'requireAuth'>) {
  return (
    <ProtectedRoute requireAuth={false} redirectTo="/dashboard" {...props}>
      {children}
    </ProtectedRoute>
  );
}